import { useEffect, useState, useRef } from "react";

import { useForm } from "react-hook-form";
import { ChevronUpIcon, ChevronDownIcon, Bars2Icon } from "@heroicons/react/24/outline";
import { useDrag, useDrop } from "react-dnd";

import Card from "../../../components/shared/Card/Card";
import { ButtonInput } from "../../../components/shared/Inputs/ButtonInput";
import { NumberInput } from "../../../components/shared/Inputs/NumberInput";
import SelectInput from "../../../components/shared/Inputs/SelectInput";
import SwitchInput from "../../../components/shared/Inputs/SwitchInput";
import LoadingWheel from "../../../components/shared/LoadingWheel";
import usePartImageSettings from "../../../data/usePartImageSettings";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import {
  DEFAULT_IMAGE_TYPE_ORDER,
  IPartImageSettings,
  PartImageRequirementType,
  partImageRequirementTypeLabels,
  WatermarkPosition,
} from "../../../models/PartImageSettings";
import { DragTypes } from "../../../models/DragTypes";

interface DraggableTypeRowProps {
  type: PartImageRequirementType;
  index: number;
  total: number;
  onMove: (from: number, to: number) => void;
}

function DraggableTypeRow({ type, index, total, onMove }: DraggableTypeRowProps) {
  const ref = useRef<HTMLLIElement>(null);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: DragTypes.IMAGE_TYPE_ORDER,
      item: { index },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [index]
  );

  const [, drop] = useDrop(
    () => ({
      accept: DragTypes.IMAGE_TYPE_ORDER,
      hover(item: { index: number }) {
        if (item.index === index) return;
        onMove(item.index, index);
        item.index = index;
      },
    }),
    [index, onMove]
  );

  drag(drop(ref));

  return (
    <li
      ref={ref}
      className={`flex items-center justify-between px-3 py-2 bg-white cursor-grab select-none${isDragging ? " opacity-40" : ""}`}
    >
      <div className="flex items-center gap-2">
        <Bars2Icon className="h-4 w-4 text-gray-300 shrink-0" />
        <span className="text-sm text-gray-800">
          <span className="text-gray-400 mr-1.5 tabular-nums">{index + 1}.</span>
          {partImageRequirementTypeLabels[type]}
        </span>
      </div>
      <div className="flex gap-1 ml-4 shrink-0">
        <button
          type="button"
          disabled={index === 0}
          onClick={() => onMove(index, index - 1)}
          className="p-0.5 rounded text-gray-400 hover:text-gray-600 disabled:opacity-25 disabled:cursor-not-allowed"
        >
          <ChevronUpIcon className="h-4 w-4" />
        </button>
        <button
          type="button"
          disabled={index === total - 1}
          onClick={() => onMove(index, index + 1)}
          className="p-0.5 rounded text-gray-400 hover:text-gray-600 disabled:opacity-25 disabled:cursor-not-allowed"
        >
          <ChevronDownIcon className="h-4 w-4" />
        </button>
      </div>
    </li>
  );
}

export default function PartImageSettingsEditor() {
  const { settings, loading, createOrUpdate, getPreview } = usePartImageSettings();
  const { saveHandlers } = useDefaultCRUDHandlers("Part Image Settings");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [typeOrder, setTypeOrder] = useState<PartImageRequirementType[]>(DEFAULT_IMAGE_TYPE_ORDER);

  const { register, handleSubmit, setValue, watch, reset, getValues } = useForm<IPartImageSettings>({
    defaultValues: {
      removeBackgrounds: false,
      numberToRemove: 0,
      replaceExistingImages: false,
      applyWatermarks: false,
      grayscale: false,
      position: WatermarkPosition.BottomRight,
      transparency: 0,
      orderByImageType: false,
      imageTypeOrder: null,
    },
  });

  // Load settings into form when they're fetched
  useEffect(() => {
    if (settings) {
      const parsedOrder = settings.imageTypeOrder
        ? (JSON.parse(settings.imageTypeOrder) as PartImageRequirementType[])
        : DEFAULT_IMAGE_TYPE_ORDER;
      setTypeOrder(parsedOrder);

      reset({
        id: settings.id,
        created: settings.created,
        updated: settings.updated,
        removeBackgrounds: settings.removeBackgrounds,
        numberToRemove: settings.numberToRemove,
        replaceExistingImages: settings.replaceExistingImages,
        applyWatermarks: settings.applyWatermarks,
        grayscale: settings.grayscale,
        position: settings.position,
        transparency: settings.transparency,
        orderByImageType: settings.orderByImageType,
        imageTypeOrder: settings.imageTypeOrder,
      });
    }
  }, [settings, reset]);

  const orderByImageType = watch("orderByImageType");

  // Watch watermark-related fields for preview
  const applyWatermarks = watch("applyWatermarks");
  const position = watch("position");
  const grayscale = watch("grayscale");
  const transparency = watch("transparency");

  // Generate preview when watermark settings change
  useEffect(() => {
    if (!applyWatermarks) {
      setPreviewUrl(null);
      return;
    }

    const generatePreview = async () => {
      const formData = getValues();
      if (!formData.applyWatermarks) return;

      setPreviewLoading(true);
      try {
        const url = await getPreview(formData as IPartImageSettings);
        // Revoke old URL to prevent memory leaks
        setPreviewUrl((prevUrl) => {
          if (prevUrl) {
            URL.revokeObjectURL(prevUrl);
          }
          return url;
        });
      } catch (error) {
        console.error("Failed to generate preview:", error);
        setPreviewUrl(null);
      } finally {
        setPreviewLoading(false);
      }
    };

    generatePreview();
  }, [applyWatermarks, position, grayscale, transparency]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const moveType = (from: number, to: number) => {
    const next = [...typeOrder];
    [next[from], next[to]] = [next[to], next[from]];
    setTypeOrder(next);
    setValue("imageTypeOrder", JSON.stringify(next));
  };

  const onSubmit = (data: IPartImageSettings) => {
    createOrUpdate.mutate({ ...data, imageTypeOrder: JSON.stringify(typeOrder) }, saveHandlers);
  };

  const watermarkPositionOptions = [
    { id: WatermarkPosition.BottomLeft, name: "Bottom Left" },
    { id: WatermarkPosition.BottomRight, name: "Bottom Right" },
    { id: WatermarkPosition.TopLeft, name: "Top Left" },
    { id: WatermarkPosition.TopRight, name: "Top Right" },
    { id: WatermarkPosition.Center, name: "Center" },
    { id: WatermarkPosition.Diagonal, name: "Diagonal" },
    { id: WatermarkPosition.Cover, name: "Cover" },
    { id: WatermarkPosition.SquareBottomLeft, name: "Square (1:1) Bottom Left" },
    { id: WatermarkPosition.SquareBottomRight, name: "Square (1:1) Bottom Right" },
    { id: WatermarkPosition.SquareTopLeft, name: "Square (1:1) Top Left" },
    { id: WatermarkPosition.SquareTopRight, name: "Square (1:1) Top Right" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <Card title="Part Image Settings" bodyClassName="p-6">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

        <div className="border-b border-gray-200 pb-6">
          <p>Once you have set up your background removal and watermark settings an automated process will run when your parts are eligible for the Unlisted Table.</p>
          <p>You can also manually trigger this process from the parts listing page in the images tab.</p>
        </div>

        {/* Background Removal Settings */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Background Removal Settings</h3>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 items-center">
            <SwitchInput
              label="Remove Backgrounds"
              register={register}
              registerName="removeBackgrounds"
              setValue={setValue}
              watch={watch}
              classes="sm:col-span-2"
            />
            <NumberInput
              label="Number to Remove"
              register={register}
              registerName="numberToRemove"
              placeholder="0"
              step="1"
              classes="sm:col-span-1"
            />
            <SwitchInput
              label="Replace Existing Images"
              register={register}
              registerName="replaceExistingImages"
              setValue={setValue}
              watch={watch}
              classes="sm:col-span-1 pt-5"
            />
          </div>
        </div>

        {/* Watermark Settings */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Watermark Settings</h3>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 items-center">
            <SwitchInput
              label="Apply Watermarks"
              register={register}
              registerName="applyWatermarks"
              setValue={setValue}
              watch={watch}
              classes="sm:col-span-2"
            />
            <SelectInput
              label="Watermark Position"
              register={register}
              registerName="position"
              options={watermarkPositionOptions}
              setValue={setValue}
              watch={watch}
              cols="sm:col-span-1"
            />
            <SwitchInput
              label="Grayscale Logo"
              register={register}
              registerName="grayscale"
              setValue={setValue}
              watch={watch}
              classes="sm:col-span-1 pt-5"
            />
            <NumberInput
              label="Transparency (0 = opaque, 99 = max transparent)"
              register={register}
              registerName="transparency"
              placeholder="0"
              step="1"
              min="0"
              max="99"
              classes="sm:col-span-2"
            />
          </div>
        </div>

        {/* Preview Section */}
        {applyWatermarks && (
          <div className="border-b border-gray-200 pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Preview</h3>
            <div className="flex justify-center">
              {previewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingWheel />
                </div>
              ) : previewUrl ? (
                <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                  <img
                    src={previewUrl}
                    alt="Watermark Preview"
                    className="max-w-full h-auto rounded"
                    style={{ maxHeight: "400px" }}
                  />
                </div>
              ) : (
                <div className="text-gray-500 py-12">Preview will appear here</div>
              )}
            </div>
          </div>
        )}

        {/* Image Ordering Settings */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Image Ordering</h3>
          <div className="space-y-4">
            <SwitchInput
              label="Order images by type"
              register={register}
              registerName="orderByImageType"
              setValue={setValue}
              watch={watch}
            />
            <p className="text-sm text-gray-500">
              When enabled, auto-reorder will sort images by their assigned type using the order below, rather than by upload time.
            </p>
            {orderByImageType && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Type order (drag or use arrows to reorder)</p>
                <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md max-w-sm">
                  {typeOrder.map((type, index) => (
                    <DraggableTypeRow
                      key={type}
                      type={type}
                      index={index}
                      total={typeOrder.length}
                      onMove={moveType}
                    />
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <ButtonInput
            label="Save Settings"
            onClick={() => { }}
            isSubmit={true}
            isLoading={createOrUpdate.isLoading}
            classes=""
          />
        </div>
      </form>
    </Card>
  );
}
